import { Link } from 'react-router-dom'
import './LegalPage.css'
import PageLayout from '../components/layout/PageLayout'
import Reveal from '../components/common/Reveal'
import { useGeneralSettings } from '../hooks/useGeneralSettings'
import { BRAND_FALLBACK, LEGAL_DOCS } from './legal/legalMeta'

const val = (v) => (typeof v === 'string' && v.trim() ? v.trim() : '')

/** First non-empty value among the given settings keys. */
const pick = (settings, ...keys) => {
  for (const key of keys) {
    const v = val(settings[key])
    if (v) return v
  }
  return ''
}

/**
 * Builds the `{contact}` clause the policy copy leaves open — " at <email> or
 * <phone>", whichever of the two the admin has set. Empty while the settings
 * request is in flight or when neither field is filled, so the sentence reads
 * "...reach out to us." rather than trailing off after a dangling "at".
 */
function contactClause(settings) {
  const parts = [
    pick(settings, 'support_email', 'email'),
    pick(settings, 'support_phone', 'phone'),
  ].filter(Boolean)
  return parts.length ? ` at ${parts.join(' or ')}` : ''
}

/**
 * The token table the legal copy is written against. Every entry has a
 * fallback that keeps the sentence true when the admin has not filled that
 * field in — an unset address reads "our registered address in India" rather
 * than naming a place we cannot verify.
 *
 * `grievance_officer_name`, `grievance_email` and `jurisdiction_city` are not
 * in the documented `/general-settings` shape yet; they are read optimistically
 * so that adding them in the admin panel is enough to make the Grievance
 * Redressal page name a real officer, with no code change here.
 */
function tokens(settings) {
  const email = pick(settings, 'support_email', 'email')
  return {
    '{company}': pick(settings, 'company_name') || BRAND_FALLBACK,
    '{site}': pick(settings, 'website_url') || 'astrorajumaharaj.com',
    '{email}': email || 'our support email address',
    '{phone}': pick(settings, 'support_phone', 'phone') || 'our support number',
    '{address}': pick(settings, 'address') || 'our registered address in India',
    '{contact}': contactClause(settings),
    '{officer}': pick(settings, 'grievance_officer_name') || 'The Grievance Officer',
    '{gemail}': pick(settings, 'grievance_email', 'support_email', 'email') || 'our support email address',
    '{city}': pick(settings, 'jurisdiction_city') || 'New Delhi',
  }
}

/** Stable anchor id for a heading, so the table of contents can link to it. */
const anchor = (heading) =>
  heading
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')

/**
 * Renderer shared by every document in `pages/legal/*`. A document is plain
 * data: `sections[]` of `{ heading, body[], list[], note, subsections[] }`.
 * Sub-sections render as h3 under their parent, and `note` renders as a
 * highlighted callout for the lines a reader must not skim past.
 */
export default function LegalPage({ badge, title, intro, updated, summary, sections, slug }) {
  const { settings } = useGeneralSettings()
  const map = tokens(settings)
  const fill = (text) =>
    Object.entries(map).reduce((acc, [token, value]) => acc.split(token).join(value), text)

  const related = LEGAL_DOCS.filter((doc) => doc.slug !== slug)

  return (
    <PageLayout className="legal-page text-astroDark antialiased overflow-x-hidden">

      {/* ── HERO / BREADCRUMB BANNER ── */}
      <header className="relative pt-[120px] sm:pt-[130px] lg:pt-[150px] pb-16 lg:pb-20 overflow-hidden bg-[#5A0808] text-white text-center">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-[70vw] h-[70vw] bg-cosmicRed/15 rounded-full animate-aura blur-3xl" />
          <svg
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[70%] aspect-square opacity-[0.06] text-goldPrimary animate-spin-slow"
            viewBox="0 0 200 200"
            fill="none"
            stroke="currentColor"
          >
            <circle cx="100" cy="100" r="98" strokeWidth="0.5" />
            <circle cx="100" cy="100" r="74" strokeWidth="0.5" strokeDasharray="2 4" />
            <circle cx="100" cy="100" r="50" strokeWidth="0.5" />
            <path d="M100 2v196M2 100h196" strokeWidth="0.3" />
          </svg>
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-6">
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="w-8 h-px bg-goldPrimary/40" />
            <span className="text-[9px] uppercase font-bold tracking-[0.4em] text-goldPrimary/70 font-royal">{badge}</span>
            <span className="w-8 h-px bg-goldPrimary/40" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-royal text-[#FBF3E4] leading-tight">{title}</h1>
          {intro && (
            <p className="text-sm sm:text-base text-white/50 font-light mt-5 max-w-xl mx-auto leading-relaxed">{fill(intro)}</p>
          )}
          <nav aria-label="Breadcrumb" className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 mt-7 px-4 text-[10px] uppercase tracking-[0.2em] font-semibold text-white/40">
            <Link to="/" className="hover:text-goldPrimary transition-colors">Home</Link>
            <span className="text-goldPrimary/50">/</span>
            <span className="text-goldPrimary">{title}</span>
          </nav>
        </div>
      </header>

      {/* ── CONTENT ── */}
      <section className="relative py-16 lg:py-20">
        <div className="max-w-3xl mx-auto px-6">
          {updated && (
            <Reveal className="text-[11px] uppercase tracking-[0.2em] font-semibold text-amber-700/70 mb-10">{updated}</Reveal>
          )}

          {/* Plain-language précis. It explains the document; it does not
              replace it, which the closing line says out loud. */}
          {summary?.length > 0 && (
            <Reveal as="aside" className="legal-summary" aria-label="In short">
              <p className="legal-summary__title">In short</p>
              <ul>
                {summary.map((item, i) => (
                  <li key={i}>{fill(item)}</li>
                ))}
              </ul>
              <p className="legal-summary__foot">
                This summary is for convenience only. The numbered sections below are what apply.
              </p>
            </Reveal>
          )}

          {sections.length > 3 && (
            <Reveal as="nav" className="legal-toc" aria-label="On this page">
              <p className="legal-toc__title">On this page</p>
              <ol>
                {sections.map((s) => (
                  <li key={s.heading}>
                    <a href={`#${anchor(s.heading)}`}>{s.heading}</a>
                  </li>
                ))}
              </ol>
            </Reveal>
          )}

          <div className="legal-prose">
            {sections.map((s) => (
              <Reveal as="section" key={s.heading} id={anchor(s.heading)}>
                <h2>{s.heading}</h2>
                {s.body?.map((p, i) => (
                  <p key={i}>{fill(p)}</p>
                ))}
                {s.list && (
                  <ul>
                    {s.list.map((item, i) => (
                      <li key={i}>{fill(item)}</li>
                    ))}
                  </ul>
                )}
                {s.subsections?.map((sub) => (
                  <div key={sub.heading} className="legal-sub">
                    <h3>{sub.heading}</h3>
                    {sub.body?.map((p, i) => (
                      <p key={i}>{fill(p)}</p>
                    ))}
                    {sub.list && (
                      <ul>
                        {sub.list.map((item, i) => (
                          <li key={i}>{fill(item)}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
                {s.note && <p className="legal-note">{fill(s.note)}</p>}
              </Reveal>
            ))}
          </div>

          {/* ── RELATED DOCUMENTS ── */}
          <Reveal as="nav" className="legal-related" aria-label="Related documents">
            <p className="legal-related__title">Related documents</p>
            <div className="legal-related__grid">
              {related.map((doc) => (
                <Link key={doc.slug} to={`/${doc.slug}`} className="legal-related__card">
                  <span className="legal-related__name">{doc.title}</span>
                  <span className="legal-related__blurb">{doc.blurb}</span>
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

    </PageLayout>
  )
}
